// Modal dialogs built on the native <dialog> element: the browser traps
// focus, makes the page behind inert and closes on Escape for us.
import { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/format.js'
import { Button } from './Button.jsx'

export function Dialog({ open, onClose, title, description, children, footer, variant = 'center', className }) {
  const ref = useRef(null)
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  // Clicking the backdrop (the dialog element itself, outside the panel) closes it.
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return undefined
    const onClick = (event) => {
      if (event.target === dialog) onClose()
    }
    const onCancel = (event) => {
      event.preventDefault()
      onClose()
    }
    dialog.addEventListener('click', onClick)
    dialog.addEventListener('cancel', onCancel)
    return () => {
      dialog.removeEventListener('click', onClick)
      dialog.removeEventListener('cancel', onCancel)
    }
  }, [onClose])

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        'bg-transparent p-0 text-ink',
        variant === 'drawer'
          ? 'ml-auto h-dvh max-h-dvh w-[min(22rem,100vw)] max-w-none'
          : 'm-auto w-[min(32rem,calc(100vw-2rem))] max-w-none',
        className,
      )}
    >
      {open && (
        <div
          className={cn(
            'flex flex-col bg-surface ring-1 ring-white/10 ring-inset',
            variant === 'drawer' ? 'h-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]' : 'max-h-[85dvh] rounded-2xl shadow-2xl shadow-black/50',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-hairline px-5 py-4">
            <div>
              <h2 id={titleId} className="font-display text-xl font-semibold tracking-wide uppercase">
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className="mt-1 text-sm text-ink-muted">
                  {description}
                </p>
              )}
            </div>
            <Button variant="ghost" size="icon" className="-mr-2 size-9" onClick={onClose} aria-label="Close">
              <X aria-hidden="true" className="size-5" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-hairline px-5 py-4">{footer}</div>}
        </div>
      )}
    </dialog>
  )
}

// Every destructive action goes through this.
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-ink">{children}</div>
    </Dialog>
  )
}
